// qs-useZipExport.ts
import JSZip from 'jszip'
import { useGalleryStore } from '@/stores/qs-store-gallery'

export interface ExportMetadata {
  appVersion: string
  exportDate: string
  totalDrawings: number
  totalSize: number
  deviceInfo: {
    userAgent: string
    screenWidth: number
    screenHeight: number
  }
}

export interface ImportResult {
  success: boolean
  importedDrawings: number
  errors: string[]
  warnings: string[]
}

interface Drawing {
  id: string
  name: string
  layers: unknown
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${Math.round(bytes / (1024 * 1024))} MB`
}

export function useZipExport() {
  const galleryStore = useGalleryStore()

  const exportAllDrawings = async (): Promise<Blob> => {
    try {
      const zip = new JSZip()
      const drawings = galleryStore.drawings

      const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.5.0'
      const metadata: ExportMetadata = {
        appVersion: version,
        exportDate: new Date().toISOString(),
        totalDrawings: drawings.length,
        totalSize: 0,
        deviceInfo: {
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        }
      }

      const drawingsJson = JSON.stringify(drawings, null, 2)
      zip.file('drawings.json', drawingsJson)
      metadata.totalSize += drawingsJson.length

      const metadataJson = JSON.stringify(metadata, null, 2)
      zip.file('metadata.json', metadataJson)

      const appInfo = {
        name: 'VectorSketch',
        version,
        description: 'Vector drawing app export',
        exportFormat: `vectorsketch-${version}`,
        compatibility: {
          minVersion: '1.0.0',
          maxVersion: '2.0.0'
        }
      }
      zip.file('app-info.json', JSON.stringify(appInfo, null, 2))

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      })

      return zipBlob
    } catch (error) {
      console.error('Error exporting drawings:', error)
      throw new Error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const downloadExport = async (): Promise<void> => {
    try {
      const zipBlob = await exportAllDrawings()
      const url = URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      link.download = `vectorsketch-backup-${timestamp}.zip`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading export:', error)
      throw error
    }
  }

  const importDrawings = async (file: File): Promise<ImportResult> => {
    const result: ImportResult = {
      success: false,
      importedDrawings: 0,
      errors: [],
      warnings: []
    }

    try {
      const zip = new JSZip()
      const zipContent = await zip.loadAsync(file)

      if (!zipContent.files['drawings.json']) {
        result.errors.push('Invalid ZIP: missing drawings.json')
        return result
      }

      if (!zipContent.files['metadata.json']) {
        result.warnings.push('No metadata.json found - using default values')
      }

      const drawingsJson = await zipContent.files['drawings.json'].async('string')
      const importedDrawings = JSON.parse(drawingsJson) as unknown[]

      if (!Array.isArray(importedDrawings)) {
        result.errors.push('Invalid drawings data: expected array')
        return result
      }

      for (let i = 0; i < importedDrawings.length; i++) {
        const drawing = importedDrawings[i] as Drawing
        if (!drawing.id || !drawing.name || !drawing.layers) {
          result.errors.push(`Invalid drawing at index ${i}: missing required fields`)
          continue
        }
      }

      if (result.errors.length > 0) {
        return result
      }

      const currentDrawings = [...galleryStore.drawings]
      const backupKey = `vectorsketch-backup-${Date.now()}`
      localStorage.setItem(backupKey, JSON.stringify(currentDrawings))

      galleryStore.drawings = importedDrawings as typeof galleryStore.drawings
      result.importedDrawings = importedDrawings.length
      result.success = true
      result.warnings.push(`Backup of current data saved as: ${backupKey}`)
    } catch (error) {
      console.error('Error importing drawings:', error)
      result.errors.push(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }

    return result
  }

  const handleImportFile = (): Promise<ImportResult> => {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.zip'
      input.onchange = async (event) => {
        const file = (event.target as HTMLInputElement).files?.[0]
        if (file) {
          const result = await importDrawings(file)
          resolve(result)
        } else {
          resolve({
            success: false,
            importedDrawings: 0,
            errors: ['No file selected'],
            warnings: []
          })
        }
      }
      input.click()
    })
  }

  const getExportSizeEstimate = (): { size: number; sizeFormatted: string } => {
    const drawings = galleryStore.drawings
    const drawingsJson = JSON.stringify(drawings)
    const size = drawingsJson.length

    return {
      size,
      sizeFormatted: formatSize(size)
    }
  }

  const getZipSizeEstimate = async (): Promise<{ size: number; sizeFormatted: string }> => {
    try {
      const zipBlob = await exportAllDrawings()
      const size = zipBlob.size

      return {
        size,
        sizeFormatted: formatSize(size)
      }
    } catch (error) {
      console.error('Error estimating ZIP size:', error)
      return { size: 0, sizeFormatted: 'Unknown' }
    }
  }

  return {
    exportAllDrawings,
    downloadExport,
    importDrawings,
    handleImportFile,
    getExportSizeEstimate,
    getZipSizeEstimate
  }
}